import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function AnalyticsDetailPage() {
    const { serverId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Configuration passed from the Dashboard card
    const { 
        title = "Metric Details", 
        collectionName, 
        metricType, // 'age', 'tenure', 'retention', 'starters', 'leads', 'status', 'churn'
        adminFilter 
    } = location.state || {};

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all'); 

    // HELPER: Robust Date Parser
    const parseDate = (value) => {
        if (!value) return null;
        if (value.toDate) return value.toDate();
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) return new Date(value);
        const d = new Date(value);
        return isNaN(d) ? null : d;
    };

    useEffect(() => {
        if (!collectionName) return;

        async function fetchData() {
            try {
                const querySnapshot = await getDocs(collection(db, 'servers', serverId, collectionName));
                let docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 1. Context Filter
                if (adminFilter) {
                    docs = docs.filter(d => d.adminID === adminFilter || d.admin === adminFilter);
                }

                // 2. Data Processing
                const processed = docs.map(doc => {
                    const isActive = doc.active !== undefined ? doc.active === true : doc.status === true;
                    const now = new Date();
                    
                    // Determine Start & End Dates
                    const startDate = parseDate(doc.startDate) || parseDate(doc.createdAt);
                    
                    // Priority for End Date logic for Churn/Tenure
                    const endDate = parseDate(doc.terminatedDate) || parseDate(doc.leftDate) || (doc.lastSeen?.toDate ? doc.lastSeen.toDate() : null);

                    let metricValue = '-';
                    let sortValue = 0;
                    let daysActive = 0;
                    let inactiveDateStr = '-';

                    // Calculate Days Active (Tenure)
                    if (startDate) {
                        const end = (!isActive && endDate) ? endDate : now;
                        const diffTime = Math.abs(end - startDate);
                        daysActive = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
                    }

                    if (!isActive && endDate) {
                        inactiveDateStr = endDate.toLocaleDateString();
                    }

                    // Metric Display Logic
                    switch (metricType) {
                        case 'age':
                            if (doc.dob) {
                                const dob = parseDate(doc.dob); 
                                if (dob) {
                                    const age = Math.floor((now - dob) / (31557600000));
                                    metricValue = `${age} Years`;
                                    sortValue = age;
                                }
                            }
                            break;
                        case 'tenure':
                        case 'starters':
                        case 'churn':
                            if (daysActive > 0) {
                                const years = (daysActive / 365).toFixed(1);
                                metricValue = `${years} Yrs (${daysActive} Days)`;
                                sortValue = daysActive;
                            }
                            break;
                        case 'leads':
                            metricValue = doc.converted ? 'Converted' : 'Open Lead';
                            break;
                        default:
                            metricValue = isActive ? 'Active' : 'Inactive';
                    }

                    return {
                        ...doc,
                        _isActive: isActive,
                        _metricValue: metricValue,
                        _sortValue: sortValue,
                        _startDate: startDate,
                        _endDate: endDate,
                        _daysActive: daysActive,
                        _inactiveDateStr: inactiveDateStr
                    };
                });

                // 3. Pre-Filter for specific cards
                let finalData = processed;
                if (metricType === 'starters') {
                    // Only show people joined in last 30 days
                    finalData = finalData.filter(d => d._sortValue <= 30);
                }
                if (metricType === 'churn') {
                    // Only inactive people
                    finalData = finalData.filter(d => !d._isActive);
                }

                setData(finalData);
            } catch (error) {
                console.error("Error fetching detail data:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [serverId, collectionName, metricType, adminFilter]);

    // --- CSV EXPORT ---
    const exportToCSV = () => {
        if (filteredList.length === 0) return;

        // Headers
        const headers = ["ID", "Name", "Email", "Status", "Metric", "Start Date", "End/Terminated Date", "Days Active"];
        
        // Rows
        const rows = filteredList.map(item => [
            item.id,
            `"${item.fullName || item.companyName || 'Unknown'}"`,
            item.email || '',
            item._isActive ? 'Active' : 'Archived',
            item._metricValue,
            item._startDate ? item._startDate.toLocaleDateString() : '',
            item._inactiveDateStr,
            item._daysActive
        ]);

        // Combine
        const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        
        // Trigger Download
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${collectionName}_analytics_export.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- VIEW LOGIC ---
    
    // Filter the list based on the dropdown
    const filteredList = data.filter(item => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'active') return item._isActive;
        if (statusFilter === 'archived') return !item._isActive;
        if (statusFilter === 'converted') return item.converted === true;
        if (statusFilter === 'open') return item.converted === false;
        return true;
    });

    const handleRowClick = (item) => {
        // Navigate to the specific entity page (Admin, Employee, etc.)
        // Assuming you have these routes or will build them. 
        // Defaulting to Admin details if it's an admin, otherwise alerts for now if routes missing.
        if (collectionName === 'admins') navigate(`/servers/${serverId}/admins/${item.id}`);
        else if (collectionName === 'employees') navigate(`/servers/${serverId}/employees/${item.id}`);
        else if (collectionName === 'serviceUsers') navigate(`/servers/${serverId}/service-users/${item.id}`);
    };

    if (loading) return <div style={{ padding: 20 }}>Loading Data Breakdown...</div>;

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button onClick={() => navigate(-1)} style={{ marginRight: '15px', padding: '8px 12px', cursor: 'pointer', border: '1px solid #ccc', background: 'white', borderRadius: '4px' }}>← Back</button>
                    <div>
                        <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '24px' }}>{title}</h1>
                        <p style={{ margin: '5px 0 0', color: '#666', fontSize: '13px' }}>
                            Calculation Breakdown & Data Source
                        </p>
                    </div>
                </div>

                {/* Actions: Export & Filter */}
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={exportToCSV} style={{ padding: '10px 15px', backgroundColor: '#2d3748', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                        ⬇ Export CSV
                    </button>
                    
                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
                    >
                        <option value="all">Show All</option>
                        {metricType === 'leads' ? (
                            <>
                                <option value="open">Open Leads</option>
                                <option value="converted">Converted</option>
                            </>
                        ) : (
                            <>
                                <option value="active">Active Only</option>
                                <option value="archived">Archived / Inactive</option>
                            </>
                        )}
                    </select>
                </div>
            </div>

            {/* Data Table */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                        <tr>
                            <th style={{ padding: '15px', fontSize: '12px', color: '#666' }}>Name</th>
                            <th style={{ padding: '15px', fontSize: '12px', color: '#666' }}>Metric Value</th>
                            <th style={{ padding: '15px', fontSize: '12px', color: '#666' }}>Status</th>
                            <th style={{ padding: '15px', fontSize: '12px', color: '#666' }}>Dates (Start ➝ End)</th>
                            <th style={{ padding: '15px', fontSize: '12px', color: '#666' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.length > 0 ? (
                            filteredList.map((item) => (
                                <tr key={item.id} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => handleRowClick(item)}>
                                    <td style={{ padding: '15px', fontWeight: 'bold', color: '#2c3e50' }}>
                                        {item.fullName || item.companyName || 'Unknown'}
                                        <div style={{ fontSize: '11px', color: '#a0aec0' }}>{item.email}</div>
                                    </td>
                                    
                                    <td style={{ padding: '15px', fontWeight: 'bold', color: '#3182ce' }}>
                                        {item._metricValue}
                                    </td>

                                    <td style={{ padding: '15px' }}>
                                        <span style={{ 
                                            padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold',
                                            backgroundColor: item._isActive ? '#f0fff4' : '#fff5f5',
                                            color: item._isActive ? '#276749' : '#c53030'
                                        }}>
                                            {item._isActive ? 'ACTIVE' : 'ARCHIVED'}
                                        </span>
                                    </td>

                                    <td style={{ padding: '15px', fontSize: '13px', color: '#555' }}>
                                        {item._startDate ? item._startDate.toLocaleDateString() : '?'} {' ➝ '} 
                                        {item._isActive ? 'Present' : item._inactiveDateStr}
                                    </td>

                                    <td style={{ padding: '15px' }}>
                                        <button style={{ padding: '6px 12px', border: '1px solid #3182ce', color: '#3182ce', background: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
                                    No records found matching filter.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}